import { Component, DoCheck, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Utilities } from "@tylertech/shared-module";
import { GridMetadata, ClassMetadata, MetadataSummary, IViewMetadata, IMetadataResequenceAction } from "@tylertech/tyler-api-types";
import { ModifierCommandArgs, RepositoryFormData } from "../../interfaces";
import
{
    MetadataService,
    MetadataPropertyService,
    MetadataEventService,
    MetadataInputSelectService,
    GridMetadataService,
    GridEventService,
} from "../../services";
import { MetadataActionHelpers, ObjectHelpers } from "../../utilities";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { IPaginatorChangeEventData } from "@tylertech/forge";

export interface AllowDeleteFunctionArgs
{
    entity: any;
    deleteFunction(): Promise<any>;
}

@Component({
    selector: "metadata-panel-grid",
    templateUrl: "./metadataPanelGrid.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataPanelGridComponent implements OnInit, DoCheck {
    @Input()
    models: any[] = [];

    @Input()
    viewMetadata: IViewMetadata;

    @Input()
    metadata: GridMetadata;

    @Input()
    datacontroller: RepositoryFormData;

    @Input()
    viewOnly: boolean = false;

    @Output()
    customFunction = new EventEmitter<number>();

    @Output()
    copyAction = new EventEmitter<any>();

    @Output()
    customAdd = new EventEmitter<void>();

    @Output()
    modifierCallback = new EventEmitter<ModifierCommandArgs>();

    @Output()
    allowDeleteCheckFunction = new EventEmitter<AllowDeleteFunctionArgs>();

    @Input()
    allowDeleteBindingSet: boolean = false;

    @Input()
    canAdd: boolean;

    @Input()
    focusFirst: boolean;

    @Input()
    dense: boolean = false;

    @Input()
    noWrap: boolean = false;

    @Input()
    hasEditFunction: boolean = false;

    @Input()
    hasCustomAdd: boolean = false;

    @Input()
    compact: boolean = false;

    /** Used in template */ summaryModel: Record<string, number>;

    /** Used in template */ summaryMetadata: ClassMetadata;

    private summaryLoading: boolean;

    private resequenceAction: IMetadataResequenceAction;

    pageIndex: number = 0;

    constructor(
        private readonly $metadataService: MetadataService,
        private readonly $metadataPropertyService: MetadataPropertyService,
        private readonly $metadataInputSelectService: MetadataInputSelectService,
        $gridEventService: GridEventService,
        $gridMetadataService: GridMetadataService
    )
    {
        const self = this;
        $gridEventService.onRefreshGrid.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                self.refreshGrid();
            });

        $gridMetadataService.addRowObservable.pipe(takeUntilDestroyed()).subscribe(()=>
        {
            if (self.allowPaging)
            {
                if (self.metadata.AddToTop)
                    self.pageIndex = 0;
                else
                    self.pageIndex = self.maxPage - 1;
            }
        });
    }

    ngOnInit(): void
    {
        this.initializeSummaryMetadata();
        this.resequenceAction = this.viewMetadata?.Actions?.find(a => MetadataActionHelpers.isResequenceAction(a));
    }

    ngDoCheck(): void
    {
        if (!Utilities.IsNullOrUndefined(this.summaryModel) && !Utilities.IsNullOrUndefined(this.summaryMetadata))
        {
            const properties = this.summaryMetadata.Properties.filter(property => !!property.SummaryType);
            for (const property of properties)
            {
                this.summaryModel[property.Name] = 0;
                if (this.models.length > 0)
                    this.models
                        .filter(
                            x =>
                                !!x &&
                                (Utilities.IsNullOrUndefined(x.SerializedRowState) ||
                                    x.SerializedRowState !== "Deleted") &&
                                !this.$metadataPropertyService.isHidden(property, x, null)
                        )
                        .forEach(x => (this.summaryModel[property.Name] += x[property.Name]));
            }
        }
    }

    get pagingTop(): boolean
    {
        return this.metadata.PagingTop;
    }

    get showHeader(): boolean
    {
        return this.metadata.CanAdd || (this.metadata.Title !== undefined && this.metadata.Title.length > 0);
    }

    get layoutAlign(): string
    {
        return this.compact ? "start center" : "start start";
    }

    refreshGrid(): void
    {
        this.sorted = false;
    }

    sorted: boolean = false;
    getIndex(model: any) :number {
        return this.shownModels.indexOf(model);
    }

    get shownModels(): any[]
    {
        if (!this.sorted)
        {
            const hasSort = !Utilities.IsNullOrUndefined(this.metadata.Sort);

            if (hasSort || !!this.resequenceAction)
            {
                const direction = Utilities.IsNullOrUndefined(this.metadata.Sort?.Direction)
                    ? "ASC"
                    : this.metadata.Sort.Direction;
                if (this.models)
                {
                    this.models = this.models.sort((a, b) => this.compare(a, b, this.metadata.Sort?.Name ?? this.resequenceAction.SequenceProperty.Name, direction));
                    this.sorted = this.models.length > 0;
                }
            }
        }
        const notDeletedModels = this.models
            ? this.models.filter(x => !x || !x.SerializedRowState || x.SerializedRowState !== "Deleted")
            : [];
        const pageSize = this.rowsPerPage;
        return this.allowPaging
            ? notDeletedModels.slice(this.pageIndex * pageSize, (this.pageIndex + 1) * pageSize)
            : notDeletedModels;
    }

    compare(a: any, b: any, orderBy: string, direction: string): number
    {
        let aVal = a[orderBy];
        let bVal = b[orderBy];
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        if (direction === "DESC") return aVal > bVal ? -1 : 1;
        else return aVal < bVal ? -1 : 1;
    }

    fireCustomFunction(index: number): void
    {
        this.customFunction.emit(index);
        this.refreshGrid();
    }

    callbackOld(model, propertyChanged, propertyValue): void {
        this.callback(<ModifierCommandArgs>{
            model: model,
            propertyChanged: propertyChanged,
            propertyValue: propertyValue,
        });
    }

    callback(args: ModifierCommandArgs): void
    {
        this.refreshGrid();
        if (Utilities.IsNullOrUndefined(this.modifierCallback))
        {
            return;
        }
        this.modifierCallback.emit(args);
    }

    canAddProperty(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.canAdd))
        {
            return this.metadata.CanAdd;
        }
        return this.canAdd;
    }

    canEdit(model: any): boolean
    {
        if (Utilities.IsNullOrUndefined(this.metadata)) return false;
        if (model.SerializedRowState === "Added" && this.canAdd) return true;
        const itemEnabled =
            Utilities.IsNullOrUndefined(model) ||
            Utilities.IsNullOrUndefinedOrEmpty(this.metadata.CanEditProperty) ||
            this.$metadataService.EvaluateBoolean(this.metadata.CanEditProperty, null, model);
        return !this.viewOnly && this.metadata.CanEdit && itemEnabled;
    }

    copy(index: number): void
    {
        if (Utilities.IsNullOrUndefined(this.copyAction))
        {
            return;
        }
        this.copyAction.emit(this.shownModels[index]);
        this.refreshGrid();
    }

    delete(toDelete: any): void
    {
        if (this.viewOnly && !toDelete)
        {
            return;
        }
        const self = this;
        if (this.allowDeleteBindingSet)
            this.allowDeleteCheckFunction.emit(<AllowDeleteFunctionArgs>{
                entity: toDelete,
                deleteFunction: () => self.deleteRow(toDelete),
            });
        else this.deleteRow(toDelete);
    }

    deleteRow(toDelete: any): Promise<any>
    {
        this.callbackOld(toDelete, "SerializedRowState", "Deleted");
        if ("SerializedRowState" in toDelete && toDelete.SerializedRowState !== "Added")
        {
            toDelete.SerializedRowState = "Deleted";
        }
        //this.models was modified in the callback
        else
        {
            this.$metadataInputSelectService.RemoveOption(this.metadata.ClassMetadata, toDelete);
            // If item is on the first page, remove it directly
            this.models.splice(this.models.indexOf(toDelete), 1);
        }
        this.callbackOld(toDelete, "RemovedFromGrid", null);
        return Promise.resolve();
    }

    addRow(): void
    {
        if (this.viewOnly) return;
        if (this.hasCustomAdd)
        {
            this.customAdd.emit();

            if (this.allowPaging)
            {
                if (this.metadata.AddToTop)
                    this.pageIndex = 0;
                else
                    this.pageIndex = this.maxPage - 1;
            }

        }
        else
        {
            const object = {};
            for (const prop of this.metadata.ClassMetadata.Properties)
            {
                object[prop.Name] = "";
            }
            if (this.metadata.AddToTop)
            {
                this.models.unshift(object);
                if (this.allowPaging)
                    this.pageIndex = 0;
            }
            else
            {
                this.models.push(object);
                if (this.allowPaging)
                    this.pageIndex = this.maxPage - 1;
            }
        }
    }

    summaryCallback(propertyName: any): void
    {
        const property = this.summaryMetadata.Properties.find(property => property.Name === propertyName);
        if (!!property && !!property.SummaryType)
        {
            this.summaryModel[propertyName] = this.models
                .filter(
                    x => !Utilities.IsNullOrUndefined(x) && !this.$metadataPropertyService.isHidden(property, x, null)
                )
                .reduce((total, x) =>
                {
                    total += x[propertyName];
                    return total;
                }, 0);
        }
    }

    initializeSummaryMetadata(): void
    {
        this.summaryLoading = true;
        this.summaryMetadata = deepCopy(this.metadata.ClassMetadata);
        this.summaryModel = {};
        this.summaryMetadata.Properties.forEach(property =>
        {
            switch (property.SummaryType)
            {
                case MetadataSummary.Sum:
                    this.summaryModel[property.Name] = 0;
                    if (!property.Label.toLowerCase()
                        .includes("total")) property.Label = "Total " + property.Label;
                    this.summaryCallback(property.Name);
                    break;

                case MetadataSummary.Count:
                    //TODO: Implement Counts.
                    break;

                case MetadataSummary.Average:
                    //TODO: Implement Average.
                    break;
                default:
                    break;
            }
        });
        function deepCopy<T>(thing: T): T
        {
            return JSON.parse(ObjectHelpers.stringifyCircular(thing));
        }
    }

    summaryMetadataExists(): boolean
    {
        if (!this.summaryMetadata)
        {
            return false;
        }
        const summaryProperties = this.summaryMetadata.Properties || [];
        if (!!this.models && this.models.length > 0 && summaryProperties.some(property => !!property.SummaryType))
        {
            if (this.summaryLoading)
            {
                const keys = Object.keys(this.summaryModel);
                keys.forEach(key =>
                {
                    this.summaryCallback(key);
                });
                this.summaryLoading = false;
            }
            return true;
        }
        return false;
    }

    get maxPage(): number
    {
        return Math.ceil(this.models.length / this.rowsPerPage);
    }

    get allowPaging(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.metadata))
        {
            return false;
        }
        return this.metadata.AllowPaging && this.maxPage > 1;
    }

    get rowsPerPage(): number
    {
        if (
            Utilities.IsNullOrUndefined(this.metadata) ||
            Utilities.IsNullOrUndefined(this.metadata.RowsPerPage) ||
            this.metadata.RowsPerPage === 0
        )
        {
            return 10;
        }
        return Math.max(this.metadata.RowsPerPage, 1);
    }

    hasDeleteAction(): boolean
    {
        return !this.viewOnly && this.metadata.CanDelete;
    }

    get numberOfActions(): number
    {
        let numberOfActions =
            Utilities.IsNullOrUndefined(this.metadata) || Utilities.IsNullOrUndefined(this.metadata.Actions)
                ? 0
                : this.metadata.Actions.length;
        if (this.hasDeleteAction())
        {
            numberOfActions += 1;
        }
        if (this.hasEditFunction)
        {
            numberOfActions += 1;
        }
        return numberOfActions;
    }

    paginate($event: IPaginatorChangeEventData): void
    {
        this.pageIndex = $event.pageIndex;
    }
}
